import { useRef, type ReactNode } from 'react'
import { panelState, type QueryReachability } from '../../lib/queryState'
import { PanelError, SectionError } from '../errors/PanelError'
import { BusyLine } from './BusyLine'
import { useBusyLine } from './useBusyLine'
import { Section, TOKENS } from './dashboard-primitives'

/**
 * One panel's async state, rendered: a skeleton while a source is still coming
 * back, the content once there is something to draw, the house error when a
 * source failed and there is nothing to draw, and the empty state otherwise.
 * The order is `panelState`'s, including its two hard-won rules: a paused
 * request (TanStack parks the retry when it thinks the browser is offline) is a
 * failure, and cached content wins over a refresh that failed.
 *
 * **The error branch is not an input.** A panel supplies what it loads, what it
 * looks like while loading, what it says when the account is genuinely empty,
 * and a noun phrase naming itself. It cannot supply what it shows on failure,
 * because the failure this module exists to prevent is a panel choosing to show
 * "you have never trained" on a request that never came back. Withholding the
 * slot is the mechanism (Spec 2 D5, #169).
 *
 * `children` is a function so that the content is only built when there is
 * content: a panel's content can then read its data without guarding against
 * undefined. `empty` defaults to nothing, for panels where zero is real data and
 * a loaded response always has something to draw.
 */
export function AsyncPanel({
  sources,
  hasContent,
  subject,
  skeleton,
  empty = null,
  children,
}: {
  sources: Partial<QueryReachability>[]
  hasContent: boolean
  /** A capitalised noun phrase that opens the error sentence: "Your attendance". */
  subject: string
  skeleton: ReactNode
  empty?: ReactNode
  children: () => ReactNode
}) {
  switch (panelState(sources, hasContent)) {
    case 'loading':
      return <>{skeleton}</>
    case 'ready':
      return <>{children()}</>
    case 'error':
      return <PanelError subject={subject} sources={sources} />
    case 'empty':
      return <>{empty}</>
  }
}

/**
 * A panel that is a whole dashboard section, heading and all, and has no
 * footprint until it has something to say: the body-composition strip (03),
 * which a user with no scans never sees, and the analysis (06 to 08), which
 * nobody sees before one is generated.
 *
 * It draws nothing while loading and nothing when empty, on purpose: while the
 * request is out nobody can tell whether this user has a scan or an analysis,
 * so a skeleton would flash and vanish for every user without one. When the
 * request failed and there is nothing to draw, it draws the section's head with
 * the house note under it, the same shape a collapsed dashboard section takes;
 * otherwise it draws `children`, which render their own section or sections.
 *
 * Same contract as `AsyncPanel`, one level up: **the error branch is not an
 * input.** Both sections used to check the state by hand and write the note out
 * themselves, which held "no panel supplies its own failure" by convention
 * rather than by construction (Spec 2 D5, #214). `head` is data, not a render
 * slot, so a caller can name the section but cannot choose what it says.
 */
export function AsyncSection({
  head,
  sources,
  hasContent,
  subject,
  children,
}: {
  /** The section's head, drawn above the note when the request failed. */
  head: { index: string; eyebrow: string; title?: string }
  sources: Partial<QueryReachability>[]
  hasContent: boolean
  /** A capitalised noun phrase that titles the note: "Your body composition". */
  subject: string
  children: () => ReactNode
}) {
  switch (panelState(sources, hasContent)) {
    case 'loading':
    case 'empty':
      return null
    case 'ready':
      return <>{children()}</>
    case 'error':
      return (
        <Section index={head.index} eyebrow={head.eyebrow} title={head.title}>
          <div className="mt-2">
            <SectionError subject={subject} sources={sources} />
          </div>
        </Section>
      )
  }
}

/**
 * The frame every panel skeleton sits in: one `status` region, named for what
 * is loading, so a screen reader hears "Loading attendance" rather than silence
 * and a test can find the skeleton without knowing its shapes. The shapes are
 * the panel's own, in its footprint.
 *
 * It is also where "busy" is said (#392, CAP-O26): when the server has refused
 * a read as busy twice, the first skeleton on the page (in reading order) draws
 * `BusyLine` above its shapes, inside this region so it is announced. Every
 * panel that waits on the house skeleton gets it; a surface with its own loading
 * state (the coach page's spinner and typing dots, the catalog and first-run
 * screens' spinners) does not, and a section that draws nothing while loading
 * (`AsyncSection`) has no skeleton to put it in. The page's own gate
 * (`RequireFirstRun`, which draws nothing while `/api/me` is unanswered) says it
 * for itself, so the first request of a page load is not the one that stays silent.
 */
export function PanelSkeleton({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const busy = useBusyLine(ref)
  // The label is also real text inside the region: a live region announces
  // what it contains, not its `aria-label`, so a region holding only hidden
  // shimmer blocks said nothing at all (D5 review).
  return (
    <div ref={ref} role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {busy && <BusyLine />}
      {children}
    </div>
  )
}

/**
 * The one loading shape: a block of the placeholder's footprint with a warm band
 * sweeping across it. Skeletons compose these inside a `PanelSkeleton`, which is
 * what a screen reader announces, so each block is hidden from it.
 *
 * It replaces five private copies and a builder-only export that differed only in
 * corner radius (16 on most, 12 on two Settings tabs), and three Tailwind
 * `animate-pulse` placeholders. `radius` carries that difference, so nothing
 * changes shape; a pill passes 999.
 *
 * **The animation is inline and named, on purpose.** `styles/fonts.css` stops
 * `[style*="warmShimmer"]` under `prefers-reduced-motion`, so a block honours the
 * setting only while its style attribute says `warmShimmer`. The pulse
 * placeholders were classes, which that switch never saw (Spec 2 D5, #174).
 */
export function Shimmer({
  height,
  width = '100%',
  radius = 16,
}: {
  height: number | string
  width?: number | string
  radius?: number | string
}) {
  return (
    <div
      aria-hidden
      style={{
        height,
        width,
        borderRadius: radius,
        background: `linear-gradient(90deg, ${TOKENS.creamHairline}, ${TOKENS.creamBorder}, ${TOKENS.creamHairline})`,
        backgroundSize: '200% 100%',
        animation: 'warmShimmer 2.4s ease-in-out infinite',
      }}
    />
  )
}
